/*
  Product page default size (master list #53, adam 29 Sep 2026; closes #12a).

  On arrival the size picker starts on S. If S is sold out: the next size UP in
  stock (M, then L), else the nearest smaller size in stock (XS, then XXS). If
  every size is sold out it shows S (sold out, so the back-in-stock form is for S).

  - A link that already names a variant (?variant=...) keeps that size.
  - Colour swatches carry the shopper's chosen size to the other colour
    (?size=M); it is used if that size is in stock there, else the rule above.
  - Complete the Look follows the main size (assets/complete-the-look.js applies
    the same fallback to each suggestion's own stock).

  Shopify renders the page on the first in-stock size, so this switches it once
  the variant picker is ready (same path as a shopper's click, so price, button,
  URL, sticky bar and Complete the Look all update).
*/
(() => {
  const script = document.currentScript;
  const sectionId = script && script.dataset.section;
  if (!sectionId) return;
  // #107 Meta landing page (?view=meta): no size is pre-selected; only a carried size (colour switch / ?size=) is used.
  const noDefault = script.dataset.noDefault === 'true';

  const ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];
  const rank = (value) => ORDER.indexOf(String(value).split('/')[0].trim().toUpperCase());

  // The wanted size if in stock; else the next size up in stock; else the nearest smaller.
  const pick = (inputs, wanted) => {
    const target = rank(wanted);
    const inStock = inputs.filter((input) => !input.classList.contains('disabled') && rank(input.value) > -1);
    const exact = inStock.find((input) => rank(input.value) === target);
    if (exact) return exact;
    const up = inStock.filter((input) => rank(input.value) > target).sort((a, b) => rank(a.value) - rank(b.value));
    if (up.length) return up[0];
    const down = inStock.filter((input) => rank(input.value) < target).sort((a, b) => rank(b.value) - rank(a.value));
    return down[0] || null;
  };

  const sizeFieldset = (picker) =>
    Array.from(picker.querySelectorAll('fieldset')).find((fs) => {
      const legend = fs.querySelector('legend');
      return legend && /size/i.test(legend.textContent);
    });

  // A size the shopper chose here (or carried from another colour) is passed on by the swatches.
  let chosenSize = null;

  // Wait until every deferred theme script has run (DOMContentLoaded fires after them):
  // switching earlier broke the variant update half-way (pickup-availability not yet
  // defined), leaving ADD TO BAG disabled.
  // (Deferred scripts already run with readyState 'interactive', before DOMContentLoaded,
  // so wait for the event itself; 'load' is the fallback if it has already fired.)
  const ready = new Promise((resolve) => {
    if (document.readyState === 'complete') return resolve();
    document.addEventListener('DOMContentLoaded', resolve, { once: true });
    window.addEventListener('load', resolve, { once: true });
  });

  // Applies the rule to the size picker currently on the page. Runs on page load, and again
  // after an in-place colour switch (assets/colour-switch.js, #60) with the carried size.
  const apply = (params, carriedSize) => {
    const picker = document.getElementById(`variant-selects-${sectionId}`);
    if (!picker || picker.closest('quick-add-modal')) return;
    const fieldset = sizeFieldset(picker);
    if (!fieldset) return;
    const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    if (!inputs.length) return;

    fieldset.addEventListener('change', (event) => {
      if (event.isTrusted && event.target.checked) chosenSize = event.target.value;
      if (event.target.checked && picker.hasAttribute('data-lp-nosize')) {
        picker.removeAttribute('data-lp-nosize');
        document.dispatchEvent(new CustomEvent('lp:size', { detail: { sectionId } }));
      }
    });

    if (noDefault && !params.has('variant')) {
      const carriedNow = (carriedSize || params.get('size') || '').toUpperCase();
      const carriedInput = carriedNow && inputs.find((input) => input.value.toUpperCase() === carriedNow && !input.classList.contains('disabled'));
      if (carriedInput) {
        chosenSize = carriedInput.value;
        if (!carriedInput.checked) {
          carriedInput.checked = true;
          carriedInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
      inputs.forEach((input) => { input.checked = false; });
      const legend = fieldset.querySelector('[data-selected-swatch-value]');
      if (legend) legend.textContent = '';
      picker.setAttribute('data-lp-nosize', '');
      document.dispatchEvent(new CustomEvent('lp:nosize', { detail: { sectionId } }));
      return;
    }

    if (params.has('variant')) {
      const checked = inputs.find((input) => input.checked);
      chosenSize = checked ? checked.value : null;
      return;
    }

    const carried = (carriedSize || params.get('size') || '').toUpperCase();
    let target = null;
    if (carried) {
      target = inputs.find((input) => input.value.toUpperCase() === carried && !input.classList.contains('disabled'));
      if (target) chosenSize = target.value;
    }
    if (!target) target = pick(inputs, 'S') || inputs.find((input) => rank(input.value) === rank('S'));
    if (!target) return;
    // #102: the inline early script may already have ticked this size (so shoppers never see XS);
    // still fire the change once so price, stock label, URL, sticky bar and Complete the Look update.
    const early = picker.getAttribute('data-lp-early-size');
    if (target.checked && !(early && early === target.value)) return;
    picker.removeAttribute('data-lp-early-size');

    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  };

  window.LPDefaultSize = {
    apply,
    chosenSize: () => chosenSize,
  };

  Promise.all([ready, customElements.whenDefined('variant-selects')]).then(() =>
    apply(new URLSearchParams(window.location.search))
  );

  // Colour swatches (links to the other colour's product): carry the chosen size over.
  document.addEventListener('click', (event) => {
    // Only the main product's colour swatches, not the ones on recommendation cards.
    const link = event.target.closest(
      `#ProductInfo-${sectionId} a.swatch-item, #ProductInfo-${sectionId} a.product__colors-swatch-link`
    );
    if (!link || !chosenSize) return;
    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('size', chosenSize);
    link.href = url.pathname + url.search + url.hash;
  });
})();
